import { createFileRoute, notFound } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import Markdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import remarkGfm from "remark-gfm"
import { DocsLayout } from "@/components/docs/docs-layout"
import { mdxComponents } from "@/components/docs/mdx-components"
import { DOCS_LANGUAGES } from "@/i18n/constants"
import { authFunctionMiddleware } from "@/lib/auth-middleware"
import { transformCallouts } from "@/lib/callout-transform"
import { getPage, getPageTree } from "@/lib/docs-source"

// Validate language parameter
const validateLang = (lang: string) => {
  if (!(DOCS_LANGUAGES as readonly string[]).includes(lang)) {
    throw notFound()
  }
  return lang
}

// Server function to load doc content
export const loadDocHome = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator((lang: string) => {
    validateLang(lang)
    return lang
  })
  .handler(async ({ data: lang }) => {
    // Get the index page (root of documentation)
    const page = getPage(lang, [])

    if (!page) {
      throw notFound()
    }

    return {
      title: page.title,
      description: page.description,
      content: page.content,
    }
  })

// Server function to load navigation tree
export const loadNavTree = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator((lang: string) => {
    validateLang(lang)
    return lang
  })
  .handler(async ({ data: lang }) => {
    return getPageTree(lang)
  })

export const Route = createFileRoute("/(authenticated)/docs/$lang/")({
  component: DocHomePage,
  loader: async ({ params }) => {
    const [content, navTree] = await Promise.all([
      loadDocHome({ data: params.lang }),
      loadNavTree({ data: params.lang }),
    ])
    return { ...content, navTree }
  },
})

function DocHomePage() {
  const data = Route.useLoaderData()
  const { lang } = Route.useParams()
  const currentPath = `/docs/${lang}`

  // Transform Callout JSX to HTML divs for rehype-raw
  const processedContent = transformCallouts(data.content)

  return (
    <DocsLayout currentPath={currentPath} lang={lang} navTree={data.navTree}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <Markdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={mdxComponents}
          >
            {processedContent}
          </Markdown>
        </article>
      </div>
    </DocsLayout>
  )
}
